import { FC } from 'react';
import styles from './CurrentModifierPrice.module.css';
import Table from '@/components/Tables/Table/Table';
import { CellContext } from '@tanstack/react-table';
import { GroupPrices, ModifierKey } from '@/types/menus.types';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { CurrentModifierPriceProps } from '@/pages/Franchisee/Menus/EditPriceDrawer/EditPriceTabItems/CurrentModifierPrice.types';

const CurrentModifierPrice: FC<CurrentModifierPriceProps> = ({
  tableData,
  setPriceDeviationsData,
  modifiers,
  modifierCategory,
}) => {
  const { t } = useTranslation();
  const StoreGroupLabel = ({ row }: CellContext<GroupPrices, unknown>) => (
    <div>
      {row.original.selectedStoreCount === row.original.totalStoreCount ? (
        <span>{row.original.storeGroupName}</span>
      ) : (
        <span className={styles.storeNameAndCount}>
          {`${row.original.storeGroupName}`} &nbsp;
          <div
            className={styles.storeCountStyle}
          >{`(${row.original.selectedStoreCount} / ${row.original.totalStoreCount})`}</div>
        </span>
      )}
    </div>
  );

  const modifierKeys: ModifierKey[] = modifiers.map((mod) => ({
    modifierId: mod.modifierId,
    slotId: mod.slotId,
    modifierWeightId: mod.modifierWeightId,
    variantId: mod.variantId!,
  }));

  const getInfoOutlined = (storeGroupName: any, selectedVariants: (string | undefined)[]) => (
    <InfoOutlined
      className={styles.priceRangeInfo}
      sx={{
        width: '22px',
        height: '22px',
        color: '#266DCC',
      }}
      // later assign the correct modifierCategory and modifierKeys while doing the edit screen
      onClick={() =>
        setPriceDeviationsData({
          isPriceDeviationsDrawerOpen: true,
          selectedGroupName: storeGroupName,
          modifierCategory,
          modifierKeys,
          cameFromEditDrawer: true,
        })
      }
    />
  );

  const PriceRanges = ({ row }: CellContext<GroupPrices, unknown>) => {
    const priceList = row.original.prices;
    const { storeGroupName } = row.original;
    const selectedVariants = modifiers.map((item) => item.variantId);
    const sortPrices = () => {
      if (priceList?.length >= 2) {
        for (let i = 0; i < priceList.length; i += 1) {
          if (priceList[i]?.includes('-')) {
            const [first, last] = priceList[i].split('-');
            priceList.splice(i, 1, first, last);
          }
        }
        priceList.sort((a: any, b: any) => a - b);
        return (
          !priceList.every((price) => price === row.original.prices[0]) && (
            <div className={styles.priceRange}>
              {`$${priceList[0]} - $${priceList[priceList.length - 1]} `}
              {getInfoOutlined(storeGroupName, selectedVariants)}
            </div>
          )
        );
      }
      if (priceList?.length === 1 && priceList[0]?.includes('-')) {
        const [first, last] = priceList[0].split('-');
        priceList.splice(0, 1, first, last);
        priceList.sort((a: any, b: any) => a - b);
        return (
          <div className={styles.priceRange}>
            {`$${priceList[0]} - $${priceList[priceList.length - 1]} `}{' '}
            {getInfoOutlined(storeGroupName, selectedVariants)}
          </div>
        );
      }
      return <div>{`$${priceList?.[0]}`}</div>;
    };
    return (
      <div>
        {row.original.prices?.length >= 2 &&
          row.original.prices.every((price) => price === row.original.prices[0]) &&
          !row.original.prices[0]?.includes('-') && <div>${row.original.prices[0]}</div>}
        {sortPrices()}
      </div>
    );
  };

  return (
    <div>
      <div className={styles.productInfo}>
        {t('editMenuPrice.currentPrice')}
        <Table
          data={tableData}
          columns={[
            {
              accessorKey: 'storeGroupName',
              header: 'Store Groups',
              cell: StoreGroupLabel,
            },
            {
              accessorKey: 'prices',
              header: 'Price',
              cell: PriceRanges,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default CurrentModifierPrice;
